import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Jedna strona dokumentu „VoltValue Projekt" — rama wspólna dla wszystkich.
 *
 * ═══ PO CO OSOBNY KOMPONENT ═══
 * Nagłówek bieżący i stopka mają być IDENTYCZNE na każdej stronie. Powtórzone
 * w pięciu plikach rozjeżdżają się przy pierwszej poprawce — ktoś zmieni odstęp
 * na stronie ceny i dokument przestaje się składać w całość. Tu jest jedno
 * miejsce, w którym się je zmienia.
 *
 * ═══ ŁAMANIE STRON ═══
 * `vv-strona-dokumentu` niesie `break-before: page` (patrz index.css). Pierwsza
 * strona dostaje `vv-strona-pierwsza`, które to łamanie zdejmuje — inaczej PDF
 * otwierałby się pustą kartką. Świadomie NIE opieramy się na `:first-child`:
 * strony siedzą w różnych kontenerach zakładek, więc pozycja w drzewie nie mówi
 * nic o pozycji w dokumencie. Ta pułapka już raz kosztowała nas pustą stronę.
 *
 * ═══ STOPKA PRZY DOLE KARTKI ═══
 * Na wydruku strona dostaje wysokość całej kartki (A4 bez marginesów `@page`)
 * i układ kolumnowy, więc stopka spada na sam dół, a nie klei się do treści.
 * Na ekranie tej wysokości NIE wymuszamy — pusty pas pod krótką sekcją
 * wyglądałby jak błąd wczytywania.
 */

/**
 * A4 (297 mm) minus marginesy `@page` (2 × 12 mm) z index.css.
 *
 * ═══ TO JEST BUDŻET, NIE TYLKO WYPEŁNIACZ ═══
 * `min-height` dopycha KRÓTKĄ stronę, żeby stopka usiadła przy dolnej krawędzi.
 * Ale treść WYŻSZA niż 273 mm i tak spłynie na kolejny arkusz — a ponieważ
 * następna strona ma własne `break-before: page`, ten nadmiar zostaje sam na
 * kartce i wygląda jak pusta strona w środku dokumentu.
 *
 * Trafiło się to okładce: kwadratowa ilustracja o szerokości 88 mm zajmowała
 * 88 mm wysokości i całość wychodziła ok. 275 mm. Przy dokładaniu czegokolwiek
 * do strony warto policzyć wysokość, a przy obrazkach pamiętać, że szerokość
 * podana w milimetrach jest przy kwadracie zarazem wysokością.
 */
const WYSOKOSC_ARKUSZA = 'print:min-h-[273mm]'

const StronaDokumentu = ({
  numer, zeStron, nazwaFirmy, podtytul,
  pierwsza = false, zNaglowkiem = true, children,
}) => {
  const { t } = useTranslation('projekt')
  return (
  <section
    className={`vv-strona-dokumentu vv-arkusz${pierwsza ? ' vv-strona-pierwsza' : ''}
                flex flex-col ${WYSOKOSC_ARKUSZA}
                rounded-xl border border-gray-200 px-6 sm:px-8 py-6
                print:border-0 print:rounded-none print:px-0`}
  >
    {zNaglowkiem && (
      <header className="flex items-baseline justify-between gap-4 pb-3 mb-6
                         border-b border-gray-200 text-xs">
        <span className="font-semibold truncate" style={{ color: 'var(--vv-dok-ciemny)' }}>
          {nazwaFirmy || 'VoltValue'}
        </span>
        <span className="text-gray-400 whitespace-nowrap">{podtytul}</span>
      </header>
    )}

    {/* `flex-1` odpycha stopkę do dołu kartki; `min-w-0` chroni przed
        rozpychaniem strony przez długie nazwy modeli w tabelach wewnątrz. */}
    <div className="flex-1 min-w-0">{children}</div>

    <footer className="flex items-baseline justify-between gap-4 pt-4 mt-6
                       border-t border-gray-200 text-[11px]">
      <span className="font-semibold truncate" style={{ color: 'var(--vv-dok-ciemny)' }}>
        {nazwaFirmy || 'VoltValue'}
      </span>
      <span className="text-gray-400 truncate hidden sm:inline print:inline">{podtytul}</span>
      <span className="text-gray-400 whitespace-nowrap">{t('dokument.strona', { numer, zeStron })}</span>
    </footer>
  </section>
  )
}

/** Nagłówek sekcji z kreską w kolorze firmy — powtarza się na czterech stronach. */
export const NaglowekSekcji = ({ tytul, opis }) => (
  <div className="mb-5">
    <span className="block w-10 h-1 rounded mb-3" style={{ backgroundColor: 'var(--vv-dok)' }} />
    <h2 className="text-2xl font-bold text-gray-900 leading-tight">{tytul}</h2>
    {opis && <p className="mt-1.5 text-sm text-gray-500 leading-6">{opis}</p>}
  </div>
)

export default StronaDokumentu
